import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import authService from "../services/auth-service";
import roleService from "../services/role-service";
import userService from "../services/user-service";
import IRole from "../types/role-type";
import IUser from "../types/user-type";
import { useUser } from "../contexts/UserContext";

export const useCommonFunctions = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [searchedUsers, setSearchedUsers] = useState<IUser[]>([]);
  const [searchCriteria, setSearchCriteria] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [userRoles, setUserRoles] = useState<IRole[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<IRole | null>(null);
  const [currentUser, setCurrentUser] = useState<IUser>();
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const { user, userId, userRole, handleLogout } = useUser();

  const intl = useIntl();

  /// Check the user roles and load their respective contents
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const userData = await authService.getCurrentUser();
        if (userData && userData.id) {
          setIsLoading(false);
          setCurrentUser(userData);
          setCurrentUserId(userData.id);
        } else {
          setContent("User session lost.");
          setIsLoading(false);
          handleLogout();
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setIsLoading(false);
      }
    };

    if (userRole && userId) {
      if (userRole.includes("admin")) {
        userService.getAdminBoard(userId).then(
          (response) => {
            setContent(response.data);
            setIsLoading(false);
          },
          (error) => {
            setContent(
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString()
            );
            setIsLoading(false);
          }
        );
        // admin specific data loads here
      } else if (userRole.includes("moderator")) {
        userService.getModeratorBoard(userId).then(
          (response) => {
            setContent(response.data);
            setIsLoading(false);
          },
          (error) => {
            setContent(
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString()
            );
            setIsLoading(false);
          }
        );
        // moderator specific data loads here
      } else {
        userService.getUserBoard().then(
          (response) => {
            setContent(response.data);
            setIsLoading(false);
          },
          (error) => {
            setContent(
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString()
            );
            setIsLoading(false);
          }
        );
        // user specific data loads here
      }
      // global data loads here
    } else {
      setContent("You do not have access to this page.");
      setIsLoading(false);
    }
    loadUserRoles();
    if (!user) {
      fetchUserData();
    }
  }, [handleLogout, user, userId, userRole]);

  const loadUserRoles = () => {
    roleService.getAllRoles().then(
      (response) => {
        const roles = response.data;
        setUserRoles(roles);
      },
      (error) => {
        console.error("Error fetching roles:", error);
      }
    );
  };

  const handleSearch = async () => {
    try {
      const response = await userService.searchUsers(searchCriteria);
      const foundUsers = response.data;
      setSearchedUsers(foundUsers);
      if (foundUsers.length > 0) {
        if (!foundUsers[0].id) {
          console.error("User ID is undefined");
          return;
        }
        setSelectedUser(foundUsers[0].id);
        if (foundUsers[0].roles && typeof foundUsers[0].roles[0] === "string") {
          const role = foundUsers[0].roles[0] as unknown as IRole;
          setSelectedRole(role);
        }
      } else {
        setSelectedUser("");
        setSelectedRole(null);
      }
    } catch (error) {
      console.error("Error searching users:", error);
    }
  };

  const handleRoleUpdate = () => {
    if (selectedUser && selectedRole && selectedRole.id) {
      roleService
        .updateUserRoles(selectedUser, [selectedRole])
        .then(() => {
          toast.info("Roles updated successfully.", {
            position: "bottom-right",
          });
          handleSearch(); // Refresh the user list
        })
        .catch((error) => {
          console.error("Error updating roles:", error);
        });
    }
  };

  const handleUserDelete = (userToDelete: string) => {
    const confirmMessages = {
      en: "Are you sure you want to delete this user? This action cannot be undone.",
      fi: "Haluatko varmasti poistaa tämän käyttäjän? Tätä toimintoa ei voi peruuttaa.",
    };
    const language = (localStorage.getItem("language") ?? "en") as "en" | "fi";
    const confirmMessage = confirmMessages[language] || confirmMessages.en;

    if (window.confirm(confirmMessage)) {
      userService
        .deleteUsers(userToDelete)
        .then(() => {
          toast.info("User deleted successfully", { position: "bottom-right" });
          handleSearch();
        })
        .catch((error) => {
          console.error("Error deleting user:", error);
        });
    } else {
      toast.info("User deletion cancelled.", { position: "bottom-right" });
    }
  };

  return {
    content,
    isLoading,
    userRoles,
    searchedUsers,
    selectedUser,
    currentUser,
    currentUserId,
    searchCriteria,
    selectedRole,
    intl,
    loadUserRoles,
    handleRoleUpdate,
    handleUserDelete,
    handleSearch,
    setSearchCriteria,
    setSelectedUser,
    setSelectedRole,
    setCurrentUser,
  };
};
